/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ColDef, ColumnState} from 'ag-grid-community';
import isEqual from 'lodash.isequal';
import isNil from 'lodash.isnil';
import {translate} from '@ion/core/src/locale/locale';
import {loadTasks} from '@ion/core/src/services/Connect';
import {GenericObject, JobStatus} from '@ion/core/src/types/entities';
import {Cursor, TaskResult} from '@ion/core/src/types/services';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {removeDuplicate} from '@ion/core/src/utils/ObjectUtil';
import {definitionsToState} from '@ion/dls/src/grid/ColumnResolver';
import {
  AgGridType,
  IonGrid,
  IonGridAPI
} from '@ion/dls/src/grid/IonGrid';
import {getSortModel} from '@ion/dls/src/grid/IonGridUtils';
import {GridResult} from '@ion/dls/src/grid/PaginationControls';
import {PAGE_SIZES} from '@ion/dls/src/grid/settings';
import {useColumnChooser} from '@ion/dls/src/grid/useColumnChooser';
import {useColumnState} from '@ion/dls/src/grid/useColumnState';
import useStopPageMonitor from '@ion/telemetry/src/apm/hooks/useStopPageMonitor';
import {useCurrentGlobalGroupId} from '../../hooks/useCurrentGlobalGroupId';
import {useGroupRefresh} from '../../hooks/useGroupRefresh';
import {exportTasks} from '../../store/actions/exporter';
import {dispatchGridColumnsChange} from '../../store/helpers';
import JobStatusCellRenderer from './JobStatusCellRenderer';
import {JobStatusRowEditor} from './JobStatusRowEditor';
import {
  NAME,
  SET_GRID_COLUMNS
} from './state';
import {getTaskNickName} from './TaskStatusGridHelpers';

export interface JobStatusGridProps {
  selectedJobId?: Function;
  isMultiEditEnabled?: boolean;
  accountId?: number;
};

enum SortProps {
  ASC = 'asc',
  STATUS = 'status',
  ASC_STATUS = '+status',
  DESC_STATUS = '-status'
};

/**
 * Grid to be rendered on the Admin Devices page.
 */
export const JobStatusGrid = ({
  selectedJobId,
  accountId,
  isMultiEditEnabled = false
}: JobStatusGridProps, ref) => {
  const dispatch                              = useDispatch();
  const columnState: ColumnState[]            = useSelector(({jobStatus: {grid}}) => grid?.columnState);
  const ionGridRef                            = useRef<IonGridAPI>();
  const [refreshRequired, setRefreshRequired] = useState(false);
  const refreshGrid                           = useGroupRefresh();
  const currentGlobalGroupId                  = useCurrentGlobalGroupId();
  const [allRows, setAllRows]                 = useState([]);
  const [gridApi, setGridApi]                 = useState<AgGridType>(null);
  const [hasNewTasks, setHasNewTasks]         = useState(true);
  const sortRef                               = useRef<string>(null);
  const pageMonitorComplete                   = useStopPageMonitor();

  const {definitions, defaultDefinitions} = useColumnState({
    columnDefinitions : getColumnDefinitions(),
    columnState
  });
  const {
    showColumnChooser,
    columnDefinitions,
    onColumnChooserSave,
    toggleColumnChooser
  } = useColumnChooser(definitions);

  /**
   * Returns a reference to the grids public api ..
   */
  useImperativeHandle(ref, () => ({
    toggleColumnChooser,
    onExport,
    onRefresh
  }));

  const onColumnSelectionsUpdate = useCallback(function(columns: ColDef[]): void {
    onColumnChooserSave(columns);
    dispatch(dispatchGridColumnsChange(definitionsToState(columns), NAME, SET_GRID_COLUMNS) as any);
  }, []);

  const onColumnStateUpdate = useCallback(function(state: ColumnState[]): void {
    dispatch(dispatchGridColumnsChange(state, NAME, SET_GRID_COLUMNS) as any);
  }, []);

  const onColumnChooserClose = useCallback(function(): void {
    toggleColumnChooser(false);
  }, []);

  const onExport = useCallback(() => {
    const {columns, sortOn} = ionGridRef.current?.getExportParams(false) || {};
    dispatch(exportTasks({columns, items: allRows, sortOn}) as any);
  }, [allRows]);

  /**
   * On open the drawer if sort icon is sorted(asc/desc order) then apply sorting on grid.
   */
  useEffect(() => {
    if (gridApi) {
      const {sort} = definitions?.find(({field}) => isEqual(field, SortProps.STATUS)) || {};
      handleSorting(sort);
      gridApi.setGridOption('loading', true);
      gridApi.setGridOption('datasource', getDataSource(gridApi));
      setGridApi(gridApi);
    }
  }, [gridApi]);

  /**
   * Since we use the infinite row model for displaying job / bulk activity status, we need to supply a data source to
   * fetch new rows as the user scrolls.
   *
   * In order to 'refresh' an infinite row model grid we need to reset the datasource.
   *
   * @param api any available grid api
   * @returns a datasource for an infinite row model grid
   */
  const getDataSource = (api: AgGridType) => {
    const onError = (
      successCallback: Function,
      failCallback: Function,
      err?: GenericObject[]
    ) => {
      setHasNewTasks(false);
      successCallback([], 0);
      api.setGridOption('loading', false);
      api.showNoRowsOverlay();
      failCallback(err);
    };
    let cursor: string = '';
    const updatedRows  = [];
    const dataSource = {
      getRows : async({failCallback, successCallback, startRow}) => {
        try {
          const hasEndCursor = !isEqual(cursor, Cursor.End);
          const hasTasks     = updatedRows?.length === startRow;
          if (startRow === 0) {
            cursor = '';
          } if (hasEndCursor && hasTasks) {
            const {
              tasks, cursor: newPage, errors, sortOn
            }: TaskResult = await loadTasks(currentGlobalGroupId, cursor, sortRef?.current);
            if (tasks) {
              cursor = newPage || Cursor.End;
              if (errors?.length) {
                return onError(successCallback, failCallback, errors);
              }
              const rows = constructJobStatusGridRows(tasks);
              if (sortOn === sortRef.current) {
                setAllRows(prevState => removeDuplicate('id', prevState.concat(rows)));
              }
              setHasNewTasks(!isNil(newPage));
              updatedRows.push(
                ...rows?.filter((row) => !updatedRows?.some((existingRow) => existingRow?.id === row?.id))
              );
              const lastRow = (newPage && newPage !== Cursor.End) ? -1 : (startRow + tasks.length);
              successCallback(rows, lastRow);
              pageMonitorComplete();
              if (!tasks?.length) {
                api.setGridOption('loading', false);
                api.showNoRowsOverlay();
                return;
              }
              if (startRow === 0 && cursor === Cursor.End) {
                api.setGridOption('loading', false);
                return;
              }
            }
          } else {
            const cachedRows = updatedRows.slice(startRow, startRow + 20);
            const lastRow    = updatedRows?.length >= startRow + 20 ? -1 : updatedRows?.length;
            successCallback(cachedRows, lastRow);
          }
        } catch (e) {
          return onError(successCallback, failCallback, e);
        }
        api.hideOverlay();
      }
    };
    return dataSource;
  };

  const onRefresh = useCallback(() => {
    setAllRows([]);
    if (gridApi) {
      gridApi.setGridOption('loading', true);
      gridApi.setGridOption('datasource', getDataSource(gridApi));
      setGridApi(gridApi);
    }
  }, [gridApi]);

  const onGridReady = useCallback((api: AgGridType) => {
    api?.setGridOption('datasource', getDataSource(api));
    setGridApi(api);
  }, []);

  const onSortChange = useCallback(() => {
    setAllRows([]);
    const {sort} = getSortModel(gridApi)?.find(({colId}) => isEqual(colId, SortProps.STATUS)) || {};
    handleSorting(sort);
    if (gridApi) {
      gridApi.setGridOption('loading', true);
      gridApi.setGridOption('datasource', getDataSource(gridApi));
      setGridApi(gridApi);
    }
  }, [gridApi]);

  function handleSorting(sort) {
    sortRef.current = null;
    if (!isNil(sort)) {
      sortRef.current = isEqual(sort, SortProps.ASC)
        ? encodeURIComponent(SortProps.ASC_STATUS)
        : encodeURIComponent(SortProps.DESC_STATUS);
    }
  };

  const hasLoading = useCallback(() => {
    if (hasNewTasks) {
      return gridApi?.paginationGetPageSize() < allRows?.length;
    }
    return true;
  }, [hasNewTasks, gridApi, allRows]);

  return (
    <IonGrid omitHeader
      hasCustomDatasource
      ref={ionGridRef}
      cacheBlockSize={20}
      isLoading={hasLoading()}
      refreshRequired={refreshRequired || refreshGrid}
      setRefreshRequired={setRefreshRequired}
      augmentItems={constructJobStatusGridRows}
      attributeMappings={attributeMappings}
      checkboxesEnabled={isMultiEditEnabled}
      columnDefinitions={columnDefinitions}
      defaultDefinitions={defaultDefinitions}
      showColumnChooser={showColumnChooser}
      onColumnChooserSave={onColumnSelectionsUpdate}
      onColumnsUpdated={onColumnStateUpdate}
      onColumnChooserClose={onColumnChooserClose}
      columnComponents={{
        statusCellRenderer : JobStatusCellRenderer,
        rowMenu            : item => (
          <JobStatusRowEditor jobstatus={item?.data} selectedJobId={selectedJobId} />
        )
      }}
      onGridReady={onGridReady}
      onSortChange={onSortChange}
      pageSizes={PAGE_SIZES} />
  );
};

/**
 * Converts a collection of devices and converts them to a list of items specific
 * for display within the Admin Devices Grid, while retaining all of the items existing
 * attributes,
 */
export const constructJobStatusGridRows = (jobStatus: JobStatus[]): GridResult[] => {
  return jobStatus.map(jobs => ({
    ...jobs,
    nameUI      : getTaskNickName(jobs),
    createdAtUI : formatDateTime(jobs.createdAt)
  })) as GridResult[];
};

const attributeMappings = {
  nameUI : 'name'
};

/**
 *
 * Returns column definitions for the desktop grid implementation ...
 */
const getColumnDefinitionsDesktop = (): ColDef[] => {
  return [{
    headerName : translate('jobstatus:grid.columns.name'),
    field      : 'nameUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('jobstatus:grid.columns.createdAt'),
    field      : 'createdAtUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('jobstatus:grid.columns.createdBy'),
    field      : 'username',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false

  }, {
    headerName   : translate('jobstatus:grid.columns.status'),
    field        : 'status',
    cellRenderer : 'statusCellRenderer',
    sortable     : true,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    minWidth     : 180
  }];
};

/**
 *
 * Returns column definitions for mobile grid implementation ...
 */
const getColumnDefinitionsMobile = (): ColDef[] => {
  return [{
    headerName : translate('jobstatus:grid.columns.name'),
    field      : 'nameUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('jobstatus:grid.columns.createdAt'),
    field      : 'createdAtUI',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false
  }, {
    headerName : translate('jobstatus:grid.columns.createdBy'),
    field      : 'createdBy',
    sortable   : false,
    filter     : false,
    resizable  : true,
    unSortIcon : false,
    hide       : false

  }, {
    headerName   : translate('jobstatus:grid.columns.status'),
    field        : 'status',
    cellRenderer : 'statusCellRenderer',
    sortable     : true,
    filter       : false,
    resizable    : true,
    unSortIcon   : true,
    hide         : false,
    minWidth     : 180
  }];
};

const getColumnDefinitions = () => ({
  desktop : getColumnDefinitionsDesktop(),
  mobile  : getColumnDefinitionsMobile()
});

export default forwardRef(JobStatusGrid);
